import { SpaceType } from "@components/atoms/Spacer/types";
import React, { memo, useEffect, useState } from "react";
import styled, { useTheme } from "styled-components/native";
import { TouchableOpacity, Platform } from "react-native";
import { DesignOptions } from "@components/atoms/Filters/types";
import { VDBText, Spacer } from "@components/atoms";
import { useTypographyStore } from "@redux/slices/typographySlice";
import { useDispatch } from "react-redux";
import {
  setFiltersPayload,
  removeFiltersPayload,
  useSearchFiltersAuxPayload,
} from "@redux/slices/searchFilters";
import { SelectedItemTheme } from "@components/atoms/Button/types";
import { ProductCategory } from "@utils/constants";
import { useTranslation } from "react-i18next";
import VDBIcon, { IconsVariant } from "@components/icons/VDBIcon";
import { VDBIcons } from "@utils/VDBIcons";
import Modal from "@components/atoms/Modal";
import { getProductThemeFromType } from "@components/common/Header/utils";
import { DefaultTheme } from "styled-components/native";

type SelectListItemProps = {
  sub_filters: Array<any>;
  selectionType?: string;
  displayedRows?: number;
  sectionTitle?: string;
  payloadName?: Array<string>;
  value?: any[];
  sectionName?: string;
  parentFilter?: string;
  selectedStyle?: SelectedItemTheme;
  selectedProduct: ProductCategory;
  parentKey?: string;
  subSectionName?: string;
  children?: React.ReactNode;
  selectedText?: string;
  modalTitle?: string;
  showAsPopover?: boolean;
  filterType?: DesignOptions;
  isLastItem?: boolean;
};

const SelectListItem = ({
  sub_filters,
  sectionTitle,
  sectionName,
  selectedStyle,
  selectedProduct,
  subSectionName,
  children,
  selectedText: initialSelectedText,
  modalTitle,
  showAsPopover = false,
  isLastItem,
}: SelectListItemProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [tempSelectedText, setTempSelectedText] = useState<string>("");
  const [payloadNamesToManage, setPayloadNamesToManage] = useState<
    Array<string>
  >([]);
  const [initialValues, setInitialValues] = useState<Record<string, any>>({});

  const typography = useTypographyStore();
  const theme = useTheme();
  const productTheme = getProductThemeFromType(selectedProduct);
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const auxPayload = useSearchFiltersAuxPayload();

  useEffect(() => {
    if (sub_filters && sub_filters.length > 0) {
      const allPayloadNames: string[] = [];

      const collectPayloadNames = (filter: any) => {
        if (filter.payload_name && Array.isArray(filter.payload_name)) {
          filter.payload_name.forEach((name: string) => {
            if (!allPayloadNames.includes(name)) {
              allPayloadNames.push(name);
            }
          });
        }

        if (filter.sub_filters && Array.isArray(filter.sub_filters)) {
          filter.sub_filters.forEach(collectPayloadNames);
        }

        if (filter.options && Array.isArray(filter.options)) {
          filter.options.forEach(collectPayloadNames);
        }
      };

      sub_filters.forEach(collectPayloadNames);
      setPayloadNamesToManage(allPayloadNames);
    }
  }, [sub_filters]);

  useEffect(() => {
    updateSelectedTextDisplay();
  }, [auxPayload, sectionName, payloadNamesToManage]);

  const handleOpenModal = () => {
    const currentValues: Record<string, any> = {};
    if (sectionName && payloadNamesToManage.length > 0) {
      payloadNamesToManage.forEach((name) => {
        if (auxPayload?.[sectionName]?.[name] !== undefined) {
          currentValues[name] = auxPayload[sectionName][name];
        }
      });
    }

    setInitialValues(currentValues);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    restoreInitialValues();
    setIsModalOpen(false);
  };

  const handleApply = () => {
    setIsModalOpen(false);

    updateSelectedTextDisplay();
  };

  const restoreInitialValues = () => {
    if (sectionName && payloadNamesToManage.length > 0) {
      payloadNamesToManage.forEach((name) => {
        dispatch(
          removeFiltersPayload({
            sectionName,
            payloadName: [name],
          })
        );
      });

      if (Object.keys(initialValues).length > 0) {
        dispatch(
          setFiltersPayload({
            sectionName,
            data: initialValues,
          })
        );
      }
    }
  };

  const updateSelectedTextDisplay = () => {
    if (!sectionName || !auxPayload?.[sectionName]) {
      setTempSelectedText("");
      return;
    }

    const rangeValueNames = payloadNamesToManage.filter(
      (name) =>
        name.includes("_from") ||
        name.includes("_to") ||
        name.includes("min") ||
        name.includes("max")
    );

    if (rangeValueNames.length >= 2) {
      const fromValue =
        auxPayload[sectionName][
          rangeValueNames?.find((n) => n.includes("_from") || n.includes("min"))
        ];
      const toValue =
        auxPayload[sectionName][
          rangeValueNames?.find((n) => n.includes("_to") || n.includes("max"))
        ];

      if (fromValue !== undefined && toValue !== undefined) {
        setTempSelectedText(`${fromValue} - ${toValue}`);
        return;
      }
    }

    const multiSelectionNames = payloadNamesToManage.filter(
      (name) =>
        Array.isArray(auxPayload[sectionName][name]) &&
        auxPayload[sectionName][name].length > 0
    );

    if (multiSelectionNames.length > 0) {
      let allSelectedValues: any[] = [];
      multiSelectionNames.forEach((name) => {
        if (Array.isArray(auxPayload[sectionName][name])) {
          allSelectedValues = [
            ...allSelectedValues,
            ...auxPayload[sectionName][name],
          ];
        }
      });

      if (allSelectedValues.length > 0) {
        const selectedLabels: string[] = [];

        const findLabelsForValues = (values: any[], options: any[]) => {
          if (!options || !Array.isArray(options)) return;

          options.forEach((option) => {
            if (
              option.search_payload &&
              values.includes(option.search_payload[0])
            ) {
              const label = option.label ? t(option.label) : option.name;
              if (label && !selectedLabels.includes(label)) {
                selectedLabels.push(label);
              }
            }

            if (option.sub_options) {
              findLabelsForValues(values, option.sub_options);
            }

            if (option.options) {
              findLabelsForValues(values, option.options);
            }
          });
        };

        sub_filters.forEach((filter) => {
          if (filter.options) {
            findLabelsForValues(allSelectedValues, filter.options);
          }

          if (filter.sub_options) {
            findLabelsForValues(allSelectedValues, filter.sub_options);
          }
        });

        if (selectedLabels.length === 0) {
          selectedLabels.push(
            ...allSelectedValues.slice(0, 2).map((v) => v.toString())
          );
        }

        if (selectedLabels.length === 1) {
          setTempSelectedText(selectedLabels[0]);
        } else if (selectedLabels.length === 2) {
          setTempSelectedText(`${selectedLabels[0]}, ${selectedLabels[1]}`);
        } else if (selectedLabels.length > 2) {
          const extraCount = allSelectedValues.length - 2;
          setTempSelectedText(
            `${selectedLabels[0]}, ${selectedLabels[1]} ${
              extraCount > 0
                ? `+ ${extraCount} ${t("filters.more", "more")}`
                : ""
            }`
          );
        }
        return;
      }
    }

    const singleValuePayloads = payloadNamesToManage.filter(
      (name) =>
        !Array.isArray(auxPayload[sectionName][name]) &&
        auxPayload[sectionName][name] !== undefined &&
        !rangeValueNames.includes(name)
    );

    if (singleValuePayloads.length > 0) {
      const selectedLabels: string[] = [];

      singleValuePayloads.forEach((name) => {
        const value = auxPayload[sectionName][name];

        const findLabelForValue = (
          _value: any,
          options: any[]
        ): string | null => {
          if (!options || !Array.isArray(options)) return null;

          for (const option of options) {
            if (option.search_payload && option.search_payload[0] === _value) {
              return option.label ? t(option.label) : option.name;
            }

            if (option.sub_options) {
              const subLabel = findLabelForValue(_value, option.sub_options);
              if (subLabel) return subLabel;
            }

            if (option.options) {
              const subLabel = findLabelForValue(_value, option.options);
              if (subLabel) return subLabel;
            }
          }

          return null;
        };

        sub_filters.forEach((filter) => {
          const label = findLabelForValue(
            value,
            filter.options || filter.sub_options || []
          );

          if (label && !selectedLabels.includes(label)) {
            selectedLabels.push(label);
          }
        });

        if (selectedLabels.length === 0) {
          selectedLabels.push(value.toString());
        }
      });

      if (selectedLabels.length === 1) {
        setTempSelectedText(selectedLabels[0]);
      } else if (selectedLabels.length === 2) {
        setTempSelectedText(`${selectedLabels[0]}, ${selectedLabels[1]}`);
      } else if (selectedLabels.length > 2) {
        const extraCount = selectedLabels.length - 2;
        setTempSelectedText(
          `${selectedLabels[0]}, ${selectedLabels[1]} ${
            extraCount > 0 ? `+ ${extraCount} ${t("filters.more", "more")}` : ""
          }`
        );
      }
      return;
    }

    const hasAnySelection = payloadNamesToManage.some(
      (name) => auxPayload[sectionName][name] !== undefined
    );

    if (hasAnySelection) {
      setTempSelectedText(t("filters.hasSelections", "Has selections"));
    } else {
      setTempSelectedText("");
    }
  };

  return (
    <>
      <ParentView onPress={handleOpenModal} activeOpacity={0.7}>
        <FilterView>
          <TextContainer>
            <VDBText type={typography["Body-M-R"]} color={theme["core-05"]}>
              {subSectionName || sectionTitle}
            </VDBText>

            {(initialSelectedText || tempSelectedText) && (
              <VDBText
                type={typography["Body-S-R"]}
                color={theme["core-04"]}
                style={{ marginTop: 2 }}
              >
                {initialSelectedText || tempSelectedText}
              </VDBText>
            )}
          </TextContainer>

          <VDBIcon
            name={VDBIcons.ARROW_RIGHT}
            size={24}
            color={theme["core-04"]}
            variant={IconsVariant.VDBCustom}
          />
        </FilterView>
      </ParentView>
      {!isLastItem && <Separator />}

      <Modal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        headerTitle={
          modalTitle ||
          subSectionName ||
          sectionTitle ||
          t("filters.select", "Select Options")
        }
        type={showAsPopover ? "popUp" : undefined}
        backgroundColor={theme["core-02"]}
        size={showAsPopover ? "lg" : undefined}
      >
        <ModalContainer>
          {children}
          <Spacer size={SpaceType.ExtraLarge} />
        </ModalContainer>
        <ApplyButtonContainer>
          <ApplyButton
            selectedStyle={selectedStyle}
            selectedProduct={selectedProduct}
            onPress={handleApply}
            activeOpacity={0.8}
            productTheme={productTheme}
          >
            <VDBText type={typography["H3-M"]} color={theme["core-01"]}>
              {t("filters.apply", "Apply")}
            </VDBText>
          </ApplyButton>
        </ApplyButtonContainer>
      </Modal>
    </>
  );
};

const FilterView = styled.View`
  gap: ${SpaceType.Basic}px;
  justify-content: space-between;
  flex-direction: row;
  flex-wrap: wrap;
`;

const ParentView = styled(TouchableOpacity)`
  padding-top: ${SpaceType.Basic}px;
  padding-bottom: ${SpaceType.Basic}px;
  padding-left: ${SpaceType.Double}px;
  padding-right: ${SpaceType.Double}px;
`;

const Separator = styled.View`
  height: 1px;
  background-color: ${({ theme }: { theme: DefaultTheme }) => theme["core-03"]};
  width: calc(100% - ${SpaceType.Double}px);
  margin-left: ${SpaceType.Double}px;
`;

const TextContainer = styled.View`
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  flex: 1;
`;

const ModalContainer = styled.View`
  ${Platform.OS === "web" ? "max-height: 80vh;" : ""}
  ${Platform.OS === "web" ? "overflow-y: auto;" : ""}
  background: ${({ theme }: { theme: DefaultTheme }) => theme["core-01"]};
  margin: ${SpaceType.Double}px;
  border-radius: ${SpaceType.Basic}px;
`;

const ApplyButtonContainer = styled.View`
  padding-horizontal: ${SpaceType.Double}px;
  padding-bottom: ${SpaceType.Double}px;
  width: 100%;
  border-top-width: 1px;
  border-top-color: ${({ theme }: { theme: DefaultTheme }) => theme["core-03"]};
  padding-top: ${SpaceType.Double}px;
`;

const ApplyButton = styled(TouchableOpacity)<{
  selectedStyle?: SelectedItemTheme;
  selectedProduct?: ProductCategory;
  productTheme?: string;
}>`
  background-color: ${({
    theme,
    productTheme,
  }: {
    theme: DefaultTheme;
    productTheme: string;
  }) => theme[`${productTheme}-01` as keyof DefaultTheme]};
  height: 48px;
  border-radius: ${SpaceType.Basic}px;
  align-items: center;
  justify-content: center;
`;

export default memo(SelectListItem);
